import { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Copy, Share2, Users, Clock, ArrowLeft, Play, Trophy, Crown, X } from "lucide-react";
import GameCanvas from "@/components/GameCanvas";
import AdModal from "@/components/AdModal";
import BottomNav from "@/components/BottomNav";
import ProAvatarFrame, { ProName } from "@/components/ProAvatarFrame";
import ProfileModal from "@/components/ProfileModal";
import { sounds } from "@/lib/sounds";
import { signScore, getRecaptchaToken } from "@/lib/security";

const MAX_RESUMES = 3;
const BGM_BASE_RATE = 1.0;
const BGM_MAX_RATE = 1.8;

export default function Room() {
  const { code } = useParams();
  const navigate = useNavigate();
  const roomCode = (code || "").toUpperCase();

  const [user, setUser] = useState(null);
  const [room, setRoom] = useState(null);
  const [players, setPlayers] = useState([]);
  const [leaderboard, setLeaderboard] = useState([]);
  const [loading, setLoading] = useState(true);
  const [minsLeft, setMinsLeft] = useState(60);

  const [gameStarted, setGameStarted] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [finalScore, setFinalScore] = useState(0);
  const [liveScore, setLiveScore] = useState(0);
  const [resumesUsed, setResumesUsed] = useState(0);
  const [resumeAdOpen, setResumeAdOpen] = useState(false);
  const [roomClosed, setRoomClosed] = useState(false);
  // Profile-modal open-state — user_id of the player whose VIP card is showing
  const [profileUserId, setProfileUserId] = useState(null);

  const canvasRef = useRef(null);
  const runSessionRef = useRef(null);
  const startTimeRef = useRef(null);
  const totalDurationRef = useRef(0);
  const bgmRateIntervalRef = useRef(null);

  // Music intensity ramps with game SPEED (mirrors main-game feel exactly).
  const startBgmRateWatcher = () => {
    if (bgmRateIntervalRef.current) clearInterval(bgmRateIntervalRef.current);
    bgmRateIntervalRef.current = setInterval(() => {
      const score = canvasRef.current?.currentScore() || 0;
      setLiveScore(score);
      const speed = canvasRef.current?.currentSpeed() || 7;
      const rate = Math.min(BGM_MAX_RATE, BGM_BASE_RATE + Math.max(0, (speed - 7) / 21) * 0.8);
      sounds.setBgmSpeed(rate);
    }, 200);
  };
  const stopBgmRateWatcher = () => {
    if (bgmRateIntervalRef.current) {
      clearInterval(bgmRateIntervalRef.current);
      bgmRateIntervalRef.current = null;
    }
  };
  // Cleanup on unmount
  useEffect(() => () => stopBgmRateWatcher(), []);

  // Load room + auth (auto-join)
  useEffect(() => {
    document.title = `Room ${roomCode} — DareDino Private Play`;
    (async () => {
      try {
        const me = await api.get("/auth/me");
        setUser(me.data);
        await api.post(`/rooms/${roomCode}/join`).catch(() => {});
        const res = await api.get(`/rooms/${roomCode}`);
        setRoom(res.data.room);
        setPlayers(res.data.players);
      } catch (e) {
        const status = e?.response?.status;
        if (status === 404) toast.error("Room not found");
        else if (status === 410) toast.error("This room has expired");
        else if (status === 401) return navigate("/", { replace: true });
        else toast.error("Could not load room");
        navigate("/friends", { replace: true });
      } finally {
        setLoading(false);
      }
    })();
  }, [roomCode, navigate]);

  // Poll leaderboard + players every 3 seconds — matches global leaderboard cadence.
  useEffect(() => {
    if (!room) return;
    const load = async () => {
      try {
        const [lb, r] = await Promise.all([
          api.get(`/rooms/${roomCode}/leaderboard`),
          api.get(`/rooms/${roomCode}`),
        ]);
        setLeaderboard(lb.data.entries || []);
        setPlayers(r.data.players || []);
      } catch (e) {
        if (e?.response?.status === 410) {
          forceCloseRoom();
        }
      }
    };
    load();
    const id = setInterval(load, 3000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room, roomCode, navigate]);

  // Countdown to expiry — checks every second so we can hard-close
  // running games the instant the room TTL elapses.
  useEffect(() => {
    if (!room?.expires_at) return;
    const update = () => {
      const ms = new Date(room.expires_at).getTime() - Date.now();
      const m = Math.max(0, Math.floor(ms / 60000));
      setMinsLeft(m);
      if (ms <= 0) {
        forceCloseRoom();
      }
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [room]);

  // Hard-close: stop the canvas, kill audio, freeze the UI on a "ROOM CLOSED"
  // overlay. User must click DISMISS to leave — matches the "everyone out at 10 min" rule.
  const forceCloseRoom = () => {
    if (roomClosed) return;
    try { canvasRef.current?.stop?.(); } catch {}
    stopBgmRateWatcher();
    sounds.stopBGM();
    sounds.stopRain();
    setResumeAdOpen(false);
    setRoomClosed(true);
  };

  const startGame = async () => {
    sounds.play("click");
    sounds.setBgmSpeed(BGM_BASE_RATE);
    sounds.playBGM();
    startBgmRateWatcher();
    setGameOver(false);
    setResumesUsed(0);
    setLiveScore(0);
    totalDurationRef.current = 0;
    startTimeRef.current = performance.now();
    try {
      const res = await api.post(`/rooms/${roomCode}/game/start`);
      runSessionRef.current = { nonce: res.data.nonce, clientStart: Date.now() };
    } catch {
      runSessionRef.current = null;
    }
    setGameStarted(true);
    setTimeout(() => canvasRef.current?.start(), 30);
  };

  // Rain / milestone / tier sounds — 1:1 with main game
  const [tierBadge, setTierBadge] = useState(null); // { idx, label } | null
  const tierTimerRef = useRef(null);
  const handleScoreMilestone = useCallback((type, valueOrIdx, tierLabel) => {
    if (type === "milestone") {
      sounds.play("milestone");
    } else if (type === "rain") {
      sounds.playRain(7000);
    } else if (type === "tier") {
      sounds.play("milestone");
      setTierBadge({ idx: valueOrIdx, label: tierLabel });
      if (tierTimerRef.current) clearTimeout(tierTimerRef.current);
      tierTimerRef.current = setTimeout(() => setTierBadge(null), 1600);
    }
  }, []);

  const handleGameOver = useCallback(async ({ score }) => {
    stopBgmRateWatcher();
    sounds.stopBGM();
    sounds.stopRain();
    sounds.play("crash");
    const now = performance.now();
    if (startTimeRef.current) {
      totalDurationRef.current += (now - startTimeRef.current) / 1000;
      startTimeRef.current = null;
    }
    setFinalScore(score);
    setGameOver(true);

    const session = runSessionRef.current;
    const clientEnd = Date.now();
    const events = canvasRef.current?.getEvents ? canvasRef.current.getEvents() : [];
    const obstaclesPassed = canvasRef.current?.obstaclesPassed
      ? canvasRef.current.obstaclesPassed()
      : 0;
    const body = {
      score,
      duration_seconds: Math.round(totalDurationRef.current),
      events,
      obstacles_passed: obstaclesPassed,
    };
    if (session?.nonce) {
      try {
        const hash = await signScore({
          score,
          startTime: session.clientStart,
          endTime: clientEnd,
          nonce: session.nonce,
          obstaclesPassed,
        });
        body.start_time = session.clientStart;
        body.end_time = clientEnd;
        body.nonce = session.nonce;
        body.hash = hash;
        const rc = await getRecaptchaToken("submit_room_score");
        if (rc) body.recaptcha_token = rc;
      } catch {}
    }
    try {
      const res = await api.post(`/rooms/${roomCode}/scores`, body);
      if (res.data?.is_new_best) {
        toast.success("New room best!", {
          style: { background: "#0B0C10", border: "1px solid #EAEB18", color: "#EAEB18" },
        });
      }
      const lb = await api.get(`/rooms/${roomCode}/leaderboard`);
      setLeaderboard(lb.data.entries || []);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail) toast.error(`Score rejected: ${detail}`);
    } finally {
      runSessionRef.current = null;
    }
  }, [roomCode]);

  // Resume flow: OPEN AD FIRST for free users, resume only after ad completes.
  // Pro users skip the ad entirely and resume instantly.
  const handleResumeClick = () => {
    if (resumesUsed >= MAX_RESUMES) return;
    sounds.play("click");
    if (user?.is_pro) {
      actuallyResume();
    } else {
      setResumeAdOpen(true);
    }
  };

  const actuallyResume = () => {
    setResumeAdOpen(false);
    setResumesUsed((n) => n + 1);
    // Track the room-resume ad impression on the backend for admin analytics.
    // Pro users don't watch ads, so skip logging for them.
    if (!user?.is_pro) {
      api.post("/game/ad-view", { context: "room_resume" }).catch(() => {});
    }
    setGameOver(false);
    startTimeRef.current = performance.now();
    // Preserve current tempo — do NOT reset to base rate on resume.
    sounds.playBGM();
    startBgmRateWatcher();
    setTimeout(() => canvasRef.current?.resume(), 30);
  };

  const handleRetry = () => {
    stopBgmRateWatcher();
    sounds.stopBGM();
    sounds.stopRain();
    sounds.play("click");
    setGameStarted(false);
    setGameOver(false);
    setLiveScore(0);
    canvasRef.current?.stop();
  };

  const shareCode = async () => {
    sounds.play("click");
    const url = `${window.location.origin}/room/${roomCode}`;
    const text = `Join my DareDino room! Code: ${roomCode}`;
    if (navigator.share) {
      try {
        await navigator.share({ title: "DareDino Room", text, url });
        return;
      } catch {}
    }
    try {
      await navigator.clipboard.writeText(`${text}\n${url}`);
      toast.success("Room link copied!");
    } catch {
      toast.error("Copy failed — please copy manually: " + roomCode);
    }
  };

  const copyCode = async () => {
    try {
      await navigator.clipboard.writeText(roomCode);
      toast.success(`Copied ${roomCode}`);
    } catch {}
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#050505]">
        <div className="text-[#00F0FF] font-mono-arcade text-xs uppercase tracking-[0.3em] animate-pulse">
          Loading room...
        </div>
      </div>
    );
  }

  if (!room) return null;

  const roomName = room.room_name || `${room.creator_name?.split(" ")[0] || "Player"}'s Room`;

  return (
    <div className="relative min-h-screen bg-[#050505]">
      <div className="absolute inset-0 grid-overlay opacity-25 pointer-events-none" />

      {/* Header — mirrors main Game.jsx */}
      <header className="relative z-10 flex items-center justify-between px-4 md:px-8 py-4 border-b border-[#00F0FF]/20 bg-[#0B0C10]/60 backdrop-blur">
        <button
          onClick={() => navigate("/friends")}
          className="text-[#94A3B8] hover:text-white"
          data-testid="room-back-btn"
          aria-label="Back to rooms"
        >
          <ArrowLeft size={18} />
        </button>
        <div className="hidden md:flex items-center gap-2 text-[10px] uppercase tracking-widest">
          <span className="text-[#94A3B8]">Room:</span>
          <span className="font-heading font-black tracking-[0.3em] text-[#EAEB18]" data-testid="room-header-name">{roomName}</span>
          <span className="text-[#94A3B8]">·</span>
          <span className="text-[#00F0FF]">{roomCode}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="flex items-center gap-1 text-[10px] uppercase tracking-widest text-[#00F0FF]">
            <Clock size={12} /> <span data-testid="room-time-left">{minsLeft}m</span>
          </span>
        </div>
      </header>

      <main className="relative z-10 max-w-7xl mx-auto px-4 md:px-8 py-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Game column — inline canvas + room lobby below when not playing */}
        <div className="lg:col-span-2">
          {/* Header strip — while playing shows live score; otherwise just resumes count */}
          <div className="mb-3 flex items-center justify-between">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#94A3B8]">
              [ Room Arena · <span className="text-[#EAEB18]" data-testid="room-name-strip">{roomName}</span> ]
            </div>
            {gameStarted && (
              <div className="font-mono-arcade text-sm md:text-base font-bold flex items-center gap-3">
                <span data-testid="room-current-score" className="text-[#EAEB18] glow-yellow">
                  {String(liveScore).padStart(5, "0")}
                </span>
              </div>
            )}
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#94A3B8]">
              Resumes: <span className="text-[#EAEB18]" data-testid="room-resumes-left">{MAX_RESUMES - resumesUsed}</span>
            </div>
          </div>

          <div className="relative">
            <GameCanvas
              ref={canvasRef}
              onGameOver={handleGameOver}
              onScoreUpdate={setLiveScore}
              onScoreMilestone={handleScoreMilestone}
            />
            {tierBadge && (
              <div
                data-testid="room-tier-badge"
                key={tierBadge.idx}
                className="absolute top-4 left-1/2 -translate-x-1/2 z-30 pointer-events-none tier-badge-anim"
              >
                <div className="border-2 border-[#EAEB18] bg-[#0B0C10]/95 px-5 py-2 shadow-[4px_4px_0_#FF007F]">
                  <div className="text-[9px] uppercase tracking-[0.35em] text-[#94A3B8]">
                    Tier {tierBadge.idx + 1} · Speed Up
                  </div>
                  <div className="font-heading font-black text-lg text-[#EAEB18] glow-yellow tracking-widest">
                    {tierBadge.label}
                  </div>
                </div>
              </div>
            )}
            {!gameStarted && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm">
                <button
                  data-testid="room-start-btn"
                  onClick={startGame}
                  className="btn-brutal start-run-cta rounded-none flex items-center gap-3"
                >
                  <Play size={24} strokeWidth={3} /> START RUN
                </button>
              </div>
            )}
            {gameOver && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4">
                <div className="w-full max-w-sm border-2 border-[#FF007F] bg-[#0B0C10] p-5 relative">
                  <button onClick={handleRetry} className="absolute top-2 right-2 text-[#94A3B8] hover:text-white" data-testid="room-gameover-close">
                    <X size={18} />
                  </button>
                  <div className="text-[10px] uppercase tracking-widest text-[#FF007F] mb-1">[ COLLISION ]</div>
                  <h3 className="font-heading font-black text-2xl text-white mb-3">GAME OVER</h3>
                  <div className="border border-[#00F0FF]/30 bg-[#050505] p-3 mb-3">
                    <div className="text-[10px] uppercase tracking-widest text-[#94A3B8] mb-0.5">Final Score</div>
                    <div className="font-heading font-black text-3xl text-[#EAEB18] tabular-nums" data-testid="room-final-score">
                      {String(finalScore).padStart(5, "0")}
                    </div>
                  </div>
                  {resumesUsed < MAX_RESUMES && (
                    <button
                      onClick={handleResumeClick}
                      data-testid="room-resume-btn"
                      className="w-full btn-brutal py-2.5 rounded-none flex items-center justify-center gap-2 mb-2 text-sm"
                    >
                      <Play size={14} /> {user?.is_pro ? "Resume" : "Resume with Ad"} ({MAX_RESUMES - resumesUsed})
                    </button>
                  )}
                  <button
                    onClick={handleRetry}
                    data-testid="room-retry-btn"
                    className="w-full btn-ghost-cyan py-2.5 rounded-none text-sm"
                  >
                    Back to Lobby
                  </button>
                  <p className="mt-2 text-[9px] text-center uppercase tracking-widest text-[#94A3B8]" />
                </div>
              </div>
            )}
          </div>

          {/* Lobby info — only when not actively playing */}
          {!gameStarted && (
            <>
              <div
                className="mt-4 border border-[#00F0FF]/25 bg-[#0B0C10] p-4 flex flex-col sm:flex-row sm:items-center gap-3"
                data-testid="room-code-panel"
              >
                <div className="flex-1">
                  <div className="text-[9px] uppercase tracking-[0.3em] text-[#00F0FF] mb-0.5">Room Code</div>
                  <div className="flex items-center gap-2">
                    <span data-testid="room-code-display" className="font-heading font-black text-2xl tracking-[0.3em] text-[#EAEB18]">
                      {roomCode}
                    </span>
                    <button onClick={copyCode} className="text-[#94A3B8] hover:text-white" data-testid="room-copy-btn" aria-label="Copy code">
                      <Copy size={14} />
                    </button>
                  </div>
                </div>
                <button
                  onClick={shareCode}
                  data-testid="room-share-btn"
                  className="btn-ghost-cyan px-4 py-2 text-xs rounded-none flex items-center justify-center gap-2"
                >
                  <Share2 size={14} /> Share
                </button>
              </div>

              <div className="mt-4">
                <h2 className="text-[10px] uppercase tracking-widest text-[#00F0FF] mb-2 flex items-center gap-2">
                  <Users size={12} /> Players ({players.length})
                </h2>
                <div className="flex flex-wrap gap-2" data-testid="room-players-list">
                  {players.map((p) => (
                    <button
                      type="button"
                      key={p.user_id}
                      onClick={() => setProfileUserId(p.user_id)}
                      className={`flex items-center gap-2 pl-2 pr-3 py-1.5 border transition-colors hover:brightness-125 ${
                        p.is_pro ? "border-[#EAEB18]/50 bg-[#EAEB18]/5" : "border-[#00F0FF]/25 bg-[#0B0C10]"
                      }`}
                      data-testid={`room-player-${p.user_id}`}
                    >
                      <ProAvatarFrame picture={p.picture} name={p.name} isPro={!!p.is_pro} size="sm" />
                      <span className="text-xs">
                        <ProName name={p.name} isPro={!!p.is_pro} className="text-white" />
                      </span>
                      {p.is_creator && <Crown size={10} className="text-[#EAEB18]" />}
                    </button>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Leaderboard column — always visible, right side on desktop, below on mobile */}
        <div className="lg:col-span-1">
          <div className="border border-[#EAEB18]/40 bg-[#0B0C10]">
            <div className="flex items-center gap-2 border-b border-[#EAEB18]/30 px-4 py-3">
              <Trophy size={14} className="text-[#EAEB18]" />
              <div className="font-heading font-bold text-xs uppercase tracking-widest text-white">Room Leaderboard</div>
              <span className="ml-auto text-[9px] uppercase tracking-widest text-[#94A3B8]" />
            </div>
            <div className="max-h-[520px] overflow-y-auto" data-testid="room-leaderboard">
              {leaderboard.length === 0 ? (
                <div className="p-6 text-center text-xs text-[#94A3B8]">
                  No scores yet — hit START RUN.
                </div>
              ) : (
                leaderboard.map((row) => {
                  const isTop3 = row.rank <= 3;
                  const rankColor = row.rank === 1 ? "#EAEB18" : row.rank === 2 ? "#00F0FF" : row.rank === 3 ? "#FF007F" : "#94A3B8";
                  const isMe = row.user_id === user?.user_id;
                  return (
                    <div
                      key={row.user_id}
                      data-testid={`room-lb-row-${row.rank}`}
                      className={`flex items-center gap-3 px-3 py-2.5 border-b border-[#00F0FF]/10 last:border-b-0 transition-colors ${
                        isMe ? "bg-[#EAEB18]/10 border-l-4 border-l-[#EAEB18]" : ""
                      }`}
                    >
                      <div
                        className="w-8 font-mono-arcade font-bold text-sm"
                        style={{ color: rankColor }}
                      >
                        #{String(row.rank).padStart(2, "0")}
                      </div>
                      {isTop3 && (
                        <Crown size={14} strokeWidth={2.5} style={{ color: rankColor }} />
                      )}
                      <div className="flex-1 min-w-0 flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => setProfileUserId(row.user_id)}
                          data-testid={`room-lb-avatar-btn-${row.rank}`}
                          className="focus:outline-none focus:ring-2 focus:ring-[#EAEB18]"
                        >
                          <ProAvatarFrame picture={row.picture} name={row.name} isPro={!!row.is_pro} size="sm" />
                        </button>
                        <button
                          type="button"
                          onClick={() => setProfileUserId(row.user_id)}
                          className="text-xs truncate text-left hover:text-[#EAEB18] transition-colors"
                        >
                          <ProName name={row.name} isPro={!!row.is_pro} className="text-white" />
                        </button>
                      </div>
                      <span className="font-mono-arcade font-black text-xs text-[#EAEB18] tabular-nums shrink-0">
                        {String(row.score).padStart(5, "0")}
                      </span>
                    </div>
                  );
                })
              )}
            </div>
            <div className="border-t border-[#00F0FF]/10 px-3 py-2 text-[9px] uppercase tracking-widest text-[#94A3B8]">
              Room-only
            </div>
          </div>

          {/* Post-leaderboard nav — removed; global bottom nav handles this */}
        </div>
      </main>

      <BottomNav />

      <AdModal
        open={resumeAdOpen}
        onComplete={actuallyResume}
        onClose={() => setResumeAdOpen(false)}
        reason="Watch a short ad to resume your run"
      />

      {profileUserId && (
        <ProfileModal
          userId={profileUserId}
          onClose={() => setProfileUserId(null)}
        />
      )}

      {/* Hard-close overlay — appears the instant the 10-minute room TTL elapses.
          Kicks every player (including anyone still mid-run) back to the friends list. */}
      {roomClosed && (
        <div
          data-testid="room-closed-overlay"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-sm px-4"
        >
          <div className="w-full max-w-md border-2 border-[#FF007F] bg-[#0B0C10] p-6 text-center shadow-[8px_8px_0_#FF007F]">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#FF007F] mb-2">[ SESSION ENDED ]</div>
            <h3 className="font-heading font-black text-3xl text-white mb-3">ROOM CLOSED</h3>
            <p className="text-xs text-[#94A3B8] mb-5">
              This room's 10-minute session has ended. Everyone has been sent back to the lobby.
            </p>
            <button
              data-testid="room-closed-dismiss"
              onClick={() => navigate("/friends", { replace: true })}
              className="w-full btn-brutal rounded-none py-3 text-xs uppercase tracking-widest"
            >
              Back to Rooms
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
